import { useMemo, useState } from "react";
import {
  ArrowRight,
  Bot,
  CalendarClock,
  Check,
  CircleDot,
  Lightbulb,
  ListTodo,
  Plus,
  Scale,
  Search,
  Sparkles
} from "lucide-react";
import clsx from "clsx";
import type {
  Decision,
  ProposedTask,
  Task,
  Thought,
  ThoughtCategory
} from "../types";
import { makeId, saveRecord, softDeleteRecord } from "../lib/db";
import { categoryClass, categoryLabel, THOUGHT_CATEGORIES } from "../lib/categories";
import { formatShortDate, isReviewDue, nowIso } from "../lib/time";
import {
  Button,
  EmptyState,
  PageHeader,
  Segmented,
  StatusPill
} from "../components/ui";
import { ConfirmDialog } from "../components/ui";
import { ThoughtEditor } from "../components/ThoughtEditor";
import { DecisionEditor } from "../components/DecisionEditor";
import { AiProposalDialog } from "../components/AiProposalDialog";

type ThinkingTab = "thoughts" | "decisions";

export function ThinkingView({
  thoughts,
  decisions,
  notify
}: {
  thoughts: Thought[];
  decisions: Decision[];
  notify: (message: string, tone?: "default" | "error") => void;
}) {
  const [tab, setTab] = useState<ThinkingTab>("thoughts");
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<"all" | ThoughtCategory>("all");
  const [thoughtEditor, setThoughtEditor] = useState<Thought | null | undefined>(undefined);
  const [decisionEditor, setDecisionEditor] = useState<Decision | null | undefined>(undefined);
  const [decisionDraft, setDecisionDraft] = useState<{ thought: Thought; draft: Partial<Decision> }>();
  const [deleteRecord, setDeleteRecord] = useState<Thought | Decision>();
  const [aiState, setAiState] = useState<{
    thought: Thought;
    action: "clarify_thought" | "extract_tasks";
  }>();

  const dueDecisions = decisions.filter(
    (decision) => decision.status === "active" && isReviewDue(decision.reviewAt)
  );
  const openThoughts = thoughts.filter((thought) => thought.status === "open");
  const filteredThoughts = useMemo(
    () =>
      thoughts
        .filter(
          (thought) =>
            categoryFilter === "all" || (thought.category ?? "other") === categoryFilter
        )
        .filter((thought) =>
          `${thought.title} ${thought.body} ${thought.judgment} ${thought.evidence} ${categoryLabel(thought.category)}`
            .toLowerCase()
            .includes(query.toLowerCase())
        )
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [categoryFilter, query, thoughts]
  );
  const filteredDecisions = useMemo(
    () =>
      decisions
        .filter((decision) =>
          `${decision.title} ${decision.decision} ${decision.rationale} ${decision.expectedOutcome}`
            .toLowerCase()
            .includes(query.toLowerCase())
        )
        .sort((a, b) => {
          if (a.status === "active" && b.status !== "active") return -1;
          if (b.status === "active" && a.status !== "active") return 1;
          return b.updatedAt.localeCompare(a.updatedAt);
        }),
    [decisions, query]
  );

  function thoughtSource(thought: Thought): string {
    return [
      `标题：${thought.title}`,
      thought.body && `原始想法：${thought.body}`,
      thought.facts && `已知事实：${thought.facts}`,
      thought.judgment && `判断：${thought.judgment}`,
      thought.evidence && `依据与反证：${thought.evidence}`,
      thought.nextStep && `下一步：${thought.nextStep}`
    ]
      .filter(Boolean)
      .join("\n");
  }

  async function applyProposedTasks(thought: Thought, proposals: ProposedTask[]) {
    const timestamp = nowIso();
    let firstId = "";
    for (const proposal of proposals) {
      const complete = Boolean(proposal.outcome?.trim() && proposal.nextAction?.trim());
      const task: Task = {
        id: makeId(),
        kind: "task",
        title: proposal.title,
        body: "",
        createdAt: timestamp,
        updatedAt: timestamp,
        revision: 0,
        sourceIds: [thought.id],
        status: complete ? "next" : "inbox",
        outcome: proposal.outcome ?? "",
        nextAction: proposal.nextAction ?? "",
        area: proposal.area ?? "",
        dueAt: proposal.dueAt
      };
      const saved = await saveRecord(task);
      if (!firstId) firstId = saved.id;
    }
    const current = thoughts.find((item) => item.id === thought.id) ?? thought;
    await saveRecord({
      ...current,
      status: "acted",
      convertedToId: firstId || current.convertedToId
    });
    notify(`已从想法创建 ${proposals.length} 项待办`);
  }

  async function applyNextStep(thought: Thought, nextStep: string) {
    const current = thoughts.find((item) => item.id === thought.id) ?? thought;
    await saveRecord({ ...current, nextStep });
    notify("建议下一步已写入思考记录");
  }

  return (
    <div className="view">
      <PageHeader
        eyebrow="思考与决策"
        title="把直觉变成可检验的判断"
        description="先自由写，再决定是否行动。重要决策留下当时的理由，之后才看得见判断力如何变化。"
        action={
          <div className="header-actions">
            {tab === "thoughts" ? (
              <Button type="button" variant="primary" onClick={() => setThoughtEditor(null)}>
                <Plus size={17} /> 记录想法
              </Button>
            ) : (
              <Button type="button" variant="primary" onClick={() => setDecisionEditor(null)}>
                <Plus size={17} /> 记录决策
              </Button>
            )}
          </div>
        }
      />

      <div className="summary-strip thinking-summary">
        <div>
          <span>待推进思考</span>
          <strong>{openThoughts.length}</strong>
        </div>
        <div>
          <span>决策待复盘</span>
          <strong>{decisions.filter((decision) => decision.status === "active").length}</strong>
        </div>
        <div className={clsx(dueDecisions.length > 0 && "attention")}>
          <span>已到复核日</span>
          <strong>{dueDecisions.length}</strong>
        </div>
        <div>
          <span>已形成行动</span>
          <strong>{thoughts.filter((thought) => thought.status === "acted").length}</strong>
        </div>
      </div>

      <div className="thinking-toolbar">
        <div className="thinking-tools-left">
          <Segmented
            value={tab}
            onChange={setTab}
            ariaLabel="思考与决策切换"
            options={[
              { value: "thoughts", label: "关键思考", count: thoughts.length },
              { value: "decisions", label: "决策记录", count: decisions.length }
            ]}
          />
          {tab === "thoughts" && (
            <select
              className="input compact-select category-filter"
              value={categoryFilter}
              onChange={(event) =>
                setCategoryFilter(event.target.value as "all" | ThoughtCategory)
              }
            >
              <option value="all">全部归属</option>
              {THOUGHT_CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          )}
        </div>
        <label className="search-field">
          <Search size={17} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={tab === "thoughts" ? "搜索想法" : "搜索决策"} />
        </label>
      </div>

      {tab === "thoughts" &&
        (filteredThoughts.length === 0 ? (
          <section className="surface">
            <EmptyState
              icon={<Lightbulb size={27} />}
              title="第一条关键思考，不必完整"
              description="先写下此时最值得反复想的一件事。"
              action={
                <Button type="button" variant="primary" onClick={() => setThoughtEditor(null)}>
                  记录想法
                </Button>
              }
            />
          </section>
        ) : (
          <div className="thought-grid">
            {filteredThoughts.map((thought) => (
              <article className="thought-card" key={thought.id}>
                <div className="thought-card-top">
                  <span className="thought-symbol"><Lightbulb size={18} /></span>
                  <span className={`thought-category ${categoryClass(thought.category)}`}>
                    {categoryLabel(thought.category)}
                  </span>
                  <StatusPill status={thought.status} compact />
                  <time>{formatShortDate(thought.updatedAt)}</time>
                </div>
                <button type="button" className="card-title-button" onClick={() => setThoughtEditor(thought)}>
                  {thought.title}
                </button>
                {thought.body && <p className="thought-body">{thought.body}</p>}
                {(thought.judgment || thought.facts || thought.nextStep) && (
                  <div className="thought-structure">
                    {thought.facts && <p><span>事实</span>{thought.facts}</p>}
                    {thought.judgment && <p><span>判断</span>{thought.judgment}</p>}
                    {thought.nextStep && <p><span>下一步</span>{thought.nextStep}</p>}
                  </div>
                )}
                <div className="thought-card-footer">
                  {thought.sourceUrl && (
                    <a
                      className="source-link"
                      href={thought.sourceUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ArrowRight size={14} /> 查看来源
                    </a>
                  )}
                  <button type="button" className="text-button" onClick={() => setAiState({ thought, action: "clarify_thought" })}>
                    <Sparkles size={15} /> 帮我想清楚
                  </button>
                  <button type="button" className="text-button" onClick={() => setAiState({ thought, action: "extract_tasks" })}>
                    <ListTodo size={15} /> 提取待办
                  </button>
                  <button
                    type="button"
                    className="text-button"
                    onClick={() =>
                      setDecisionDraft({
                        thought,
                        draft: {
                          title: thought.title,
                          body: thought.body,
                          decision: thought.judgment,
                          rationale: thought.evidence,
                          expectedOutcome: thought.nextStep,
                          assumptions: thought.facts,
                          reviewAt: thought.reviewAt,
                          sourceIds: [thought.id]
                        }
                      })
                    }
                  >
                    <Scale size={15} /> 转为决策
                  </button>
                </div>
              </article>
            ))}
          </div>
        ))}

      {tab === "decisions" &&
        (filteredDecisions.length === 0 ? (
          <section className="surface">
            <EmptyState
              icon={<Scale size={27} />}
              title="还没有留下决策记录"
              description="不是每件事都值得记录，但重要的取舍应该留下当时的依据。"
              action={
                <Button type="button" variant="primary" onClick={() => setDecisionEditor(null)}>
                  记录一个决策
                </Button>
              }
            />
          </section>
        ) : (
          <div className="decision-list">
            {filteredDecisions.map((decision) => {
              const due = decision.status === "active" && isReviewDue(decision.reviewAt);
              return (
                <article className={clsx("decision-card", due && "due")} key={decision.id}>
                  <div className="decision-side">
                    <CircleDot size={19} />
                    <span>{formatShortDate(decision.reviewAt)}</span>
                    {due && <strong>到复核日</strong>}
                  </div>
                  <div className="decision-main">
                    <div className="decision-title-row">
                      <button type="button" className="card-title-button" onClick={() => setDecisionEditor(decision)}>
                        {decision.title}
                      </button>
                      <StatusPill status={decision.status} compact />
                    </div>
                    <div className="decision-columns">
                      <div>
                        <span className="eyebrow">决定</span>
                        <p>{decision.decision}</p>
                      </div>
                      <div>
                        <span className="eyebrow">为什么</span>
                        <p>{decision.rationale}</p>
                      </div>
                      <div>
                        <span className="eyebrow">预期结果</span>
                        <p>{decision.expectedOutcome}</p>
                      </div>
                    </div>
                    {decision.actualOutcome && (
                      <div className="actual-outcome">
                        <Check size={16} />
                        <span><strong>实际结果：</strong>{decision.actualOutcome}</span>
                      </div>
                    )}
                  </div>
                  <button type="button" className="decision-edit" onClick={() => setDecisionEditor(decision)}>
                    {decision.status === "active" ? "复核" : "查看"} <ArrowRight size={15} />
                  </button>
                </article>
              );
            })}
          </div>
        ))}

      {thoughtEditor !== undefined && (
        <ThoughtEditor
          thought={thoughtEditor ?? undefined}
          onClose={() => setThoughtEditor(undefined)}
          onSaved={() => notify("思考已保存")}
          onDelete={setDeleteRecord}
        />
      )}

      {decisionEditor !== undefined && (
        <DecisionEditor
          decision={decisionEditor ?? undefined}
          onClose={() => setDecisionEditor(undefined)}
          onSaved={() => notify("决策记录已保存")}
          onDelete={setDeleteRecord}
        />
      )}

      {decisionDraft && (
        <DecisionEditor
          draft={decisionDraft.draft}
          onClose={() => setDecisionDraft(undefined)}
          onSaved={() => {
            void saveRecord({
              ...decisionDraft.thought,
              status: "acted"
            }).then(() => notify("决策已记录，原想法已标记为已行动"));
            setDecisionDraft(undefined);
          }}
        />
      )}

      {aiState && (
        <AiProposalDialog
          action={aiState.action}
          selectedText={thoughtSource(aiState.thought)}
          sourceLabel={aiState.thought.title}
          onClose={() => setAiState(undefined)}
          onApplyTasks={(tasks) => applyProposedTasks(aiState.thought, tasks)}
          onApplyNextStep={(nextStep) => applyNextStep(aiState.thought, nextStep)}
        />
      )}

      {deleteRecord && (
        <ConfirmDialog
          title="删除这条记录？"
          description={`“${deleteRecord.title}”会进入软删除记录，不再出现在工作台中。`}
          confirmLabel="删除"
          onClose={() => setDeleteRecord(undefined)}
          onConfirm={() => {
            void softDeleteRecord(deleteRecord.id).then(() => {
              setThoughtEditor(undefined);
              setDecisionEditor(undefined);
              setDeleteRecord(undefined);
              notify("记录已删除");
            });
          }}
        />
      )}
    </div>
  );
}
